import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { loanService } from '@/services/loan.service';
import {
  LoanCreate,
  LoansPaginatedParams,
  LoanResponse,
  LoansPaginatedResponse,
} from '@/types/api.types';
import toast from 'react-hot-toast';
import { features } from '@/config/features';

// Query keys for consistent cache management
export const loanKeys = {
  all: ['loans'] as const,
  lists: () => [...loanKeys.all, 'list'] as const,
  list: (params: LoansPaginatedParams) =>
    [...loanKeys.lists(), params] as const,
  details: () => [...loanKeys.all, 'detail'] as const,
  detail: (id: number) => [...loanKeys.details(), id] as const,
};

// Hook for fetching all loans (for client-side filtering)
async function fetchAllLoans(): Promise<LoanResponse[]> {
  let allLoans: LoanResponse[] = [];
  let currentPage = 1;
  let hasMore = true;
  
  while (hasMore) {
    const loansData = await loanService.getLoansPaginated({ 
      page: currentPage, 
      page_size: 100 
    });
    if (loansData.items && loansData.items.length > 0) {
      allLoans = [...allLoans, ...loansData.items];
      hasMore = loansData.has_next || false;
      currentPage++;
    } else {
      hasMore = false;
    }
  }
  
  return allLoans;
}

// Client-side filtering function
function filterLoans(
  loans: LoanResponse[],
  search?: string,
  sectors?: string,
  statuses?: string
): LoanResponse[] {
  let filtered = loans;

  // Apply search filter
  if (search) {
    const query = search.toLowerCase().trim();
    filtered = filtered.filter((loan) => {
      const idMatch = String(loan.id || '').includes(query);
      const rpSystemIdMatch = String(loan.rp_system_id || '').includes(query);
      const clientLoanNumberMatch = loan.client_loan_number
        ?.toLowerCase()
        .includes(query);
      const loanNameMatch = loan.loan_name?.toLowerCase().includes(query);
      const borrowerMatch = loan.sponsor_borrower?.toLowerCase().includes(query);

      const matches =
        idMatch ||
        rpSystemIdMatch ||
        clientLoanNumberMatch ||
        loanNameMatch ||
        borrowerMatch;

      // Debug logging for Client Loan Number search
      if (matches && clientLoanNumberMatch) {
        console.log('[Client Search] Found match by Client Loan Number:', {
          search: query,
          client_loan_number: loan.client_loan_number,
          id: loan.id,
          rp_system_id: loan.rp_system_id,
        });
      }

      return matches;
    });
  }

  // Apply sector filter
  if (sectors && sectors !== 'all') {
    filtered = filtered.filter((loan) => loan.property_sector === sectors);
  }

  // Apply status filter
  if (statuses && statuses !== 'all') {
    filtered = filtered.filter((loan) => loan.loan_status === statuses);
  }

  return filtered;
}

// Hook for fetching paginated loans
export function useLoans(params: LoansPaginatedParams = {}) {
  const hasSearch = Boolean(params.search);
  const hasStatusFilter = Boolean(params.statuses);
  const hasSectorFilter = Boolean(params.sectors);

  // Use client-side filtering if:
  // 1. Search is active (for Client Loan Number search)
  // 2. Status filter is active (to avoid backend errors)
  // 3. Sector filter is active (for consistency)
  if (hasSearch || hasStatusFilter || hasSectorFilter) {
    return useQuery({
      queryKey: ['loans', 'all', 'client-filtered', params.search, params.sectors, params.statuses],
      queryFn: fetchAllLoans,
      staleTime: 2 * 60 * 1000, // 2 minutes
      refetchInterval: features.liveUpdates.enabled
        ? features.liveUpdates.intervalMs
        : false,
      select: (allLoans): LoansPaginatedResponse => {
        // Apply client-side filtering
        const filtered = filterLoans(
          allLoans,
          params.search,
          params.sectors,
          params.statuses
        );

        // Apply pagination
        const page = params.page || 1;
        const pageSize = params.page_size || 20;
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedItems = filtered.slice(startIndex, endIndex);

        return {
          items: paginatedItems,
          total: filtered.length,
          page: page,
          page_size: pageSize,
          total_pages: Math.ceil(filtered.length / pageSize),
          has_next: endIndex < filtered.length,
          has_prev: page > 1,
        };
      },
    });
  }

  // Otherwise, use server-side pagination (no filters)
  return useQuery({
    queryKey: loanKeys.list(params),
    queryFn: () => loanService.getLoansPaginated(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: features.liveUpdates.enabled
      ? features.liveUpdates.intervalMs
      : false,
    placeholderData: (previousData) => previousData, // Smooth pagination
  });
}

// Hook for fetching a single loan by ID
export function useLoan(loanId: number) {
  return useQuery({
    queryKey: loanKeys.detail(loanId),
    queryFn: () => loanService.getLoanBySystemId(loanId),
    enabled: !!loanId, // Only run if loanId exists
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: features.liveUpdates.enabled
      ? features.liveUpdates.intervalMs
      : false,
  });
}

// Hook for creating a new loan
export function useCreateLoan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LoanCreate) => loanService.createLoan(data),
    onSuccess: (newLoan) => {
      // Invalidate and refetch loans list
      queryClient.invalidateQueries({ queryKey: loanKeys.lists() });
      // Also invalidate client-filtered cache
      queryClient.invalidateQueries({ queryKey: ['loans', 'all', 'client-filtered'] });

      // Add the new loan to the cache
      queryClient.setQueryData(loanKeys.detail(newLoan.rp_system_id), newLoan);

      toast.success(`Loan "${newLoan.loan_name}" created successfully`);
    },
    onError: (error: Error) => {
      console.error('Failed to create loan:', error);
      toast.error('Failed to create loan. Please try again.');
    },
  });
}

// Hook for updating a loan
export function useUpdateLoan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      systemId,
      data,
    }: {
      systemId: number;
      data: Partial<LoanCreate>;
    }) => loanService.updateLoan(systemId, data),
    onSuccess: (updatedLoan) => {
      // Update the specific loan in cache
      queryClient.setQueryData(
        loanKeys.detail(updatedLoan.rp_system_id),
        updatedLoan
      );

      // Invalidate loans list to refetch
      queryClient.invalidateQueries({ queryKey: loanKeys.lists() });
      // Also invalidate client-filtered cache
      queryClient.invalidateQueries({ queryKey: ['loans', 'all', 'client-filtered'] });

      toast.success(`Loan "${updatedLoan.loan_name}" updated successfully`);
    },
    onError: (error: Error) => {
      console.error('Failed to update loan:', error);
      toast.error('Failed to update loan. Please try again.');
    },
  });
}

// Hook for updating a loan with any data format (used by variant A form)
export function useUpdateLoanAny() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      systemId,
      data,
    }: {
      systemId: number;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: any;
    }) => loanService.updateLoanAny(systemId, data),
    onSuccess: (updatedLoan) => {
      // Update the specific loan in cache
      queryClient.setQueryData(
        loanKeys.detail(updatedLoan.rp_system_id),
        updatedLoan
      );

      // Invalidate loans list to refetch
      queryClient.invalidateQueries({ queryKey: loanKeys.lists() });
      // Also invalidate client-filtered cache
      queryClient.invalidateQueries({ queryKey: ['loans', 'all', 'client-filtered'] });

      toast.success(`Loan "${updatedLoan.loan_name}" updated successfully`);
    },
    onError: (error: Error) => {
      console.error('Failed to update loan:', error);
      toast.error('Failed to update loan. Please try again.');
    },
  });
}

// Hook for deleting a loan
export function useDeleteLoan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (systemId: number) => loanService.deleteLoan(systemId),
    onSuccess: (_, systemId) => {
      // Remove the loan from cache
      queryClient.removeQueries({ queryKey: loanKeys.detail(systemId) });

      // Invalidate loans list to refetch
      queryClient.invalidateQueries({ queryKey: loanKeys.lists() });
      // Also invalidate client-filtered cache
      queryClient.invalidateQueries({ queryKey: ['loans', 'all', 'client-filtered'] });

      toast.success('Loan deleted successfully');
    },
    onError: (error: Error) => {
      console.error('Failed to delete loan:', error);
      toast.error('Failed to delete loan. Please try again.');
    },
  });
}

// Hook for refreshing loans data
export function useRefreshLoans() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: loanKeys.lists() });
  };
}
